import type { HealthQuestionnaireQuestionsQuery } from 'src/common/api/apollo/additional';

export type GqlQuestion = HealthQuestionnaireQuestionsQuery['healthQuestionnaireQuestions'][number];
export type GqlFollowUp = NonNullable<GqlQuestion['followUp']>;
export type GqlField = NonNullable<GqlFollowUp['fields']>[number];
export type GqlCondition = NonNullable<GqlFollowUp['visibility']>;

export type NestedQuestion = NonNullable<GqlQuestion['questions']>[number];
export type DeepQuestion = NonNullable<NestedQuestion['questions']>[number];

export type AnyQuestion = GqlQuestion | NestedQuestion | DeepQuestion;

export interface TableColumn {
  value: string;
  label: string;
}

export interface TableRow {
  code: string;
  label: string;
}

export type TableSelectionMode = 'multiple_per_row' | 'single_per_row';

/**
 * Local augmentation for `table`-type questions. The GraphQL schema will expose
 * `columns`, `rows` and `selectionMode` as optional fields on `QuestionDto`; until
 * codegen picks them up we type them here so the UI can access them safely.
 */
export interface TableQuestionMeta {
  columns?: TableColumn[] | null;
  rows?: TableRow[] | null;
  selectionMode?: TableSelectionMode | string | null;
}

export type TableQuestion = AnyQuestion & TableQuestionMeta;

/** Selected column values per row, for a single table question. */
export type TableAnswer = Record<string, string[]>;

export function isTableQuestion(q: AnyQuestion): q is TableQuestion {
  return q.type?.toLowerCase() === 'table';
}

export function getTableMeta(q: AnyQuestion): TableQuestionMeta {
  const aug = q as TableQuestion;
  return {
    columns: aug.columns ?? null,
    rows: aug.rows ?? null,
    selectionMode: aug.selectionMode ?? 'multiple_per_row',
  };
}

export interface QuestionnaireAnswers {
  /** question.code → true = yes, false = no */
  questions: Record<string, boolean>;
  /** question.code → selected child codes (for checkboxes) */
  checkboxes: Record<string, string[]>;
  /** question.code → free-text / number value (for input_text, input_number, input_string) */
  textInputs: Record<string, string>;
  /** question.code → { rowCode → selected column values[] } (for table) */
  tables: Record<string, TableAnswer>;
  /** followUp.code → { fieldCode: value } */
  followUpFields: Record<string, Record<string, string>>;
  confirmed: boolean;
}

export const EMPTY_ANSWERS: QuestionnaireAnswers = {
  questions: {},
  checkboxes: {},
  textInputs: {},
  tables: {},
  followUpFields: {},
  confirmed: false,
};

export function findQuestionByCode(
  questions: AnyQuestion[],
  code: string,
): AnyQuestion | undefined {
  for (const q of questions) {
    if (q.code === code) return q;
    const children = 'questions' in q ? (q as GqlQuestion).questions : undefined;
    if (children?.length) {
      const found = findQuestionByCode(children as AnyQuestion[], code);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * For `any_nested_answer` / count conditions: how many nested answers match under `parentQuestionCode`.
 * Uses the question tree so group_of_questions only counts its children (not every yes in the form).
 */
function countForParentCondition(
  parentQuestionCode: string,
  answers: QuestionnaireAnswers,
  allQuestions: AnyQuestion[] | undefined,
): number {
  const checkboxCount = (answers.checkboxes[parentQuestionCode] ?? []).length;
  if (checkboxCount > 0) return checkboxCount;

  if (!allQuestions?.length) {
    return Object.values(answers.questions).filter(Boolean).length;
  }

  const parent = findQuestionByCode(allQuestions, parentQuestionCode);
  const parentType = parent?.type?.toLowerCase();
  const children =
    parent && 'questions' in parent ? (parent as GqlQuestion).questions ?? [] : [];

  if (
    parentType === 'group_of_questions' ||
    parentType === 'group' ||
    parentType === 'section'
  ) {
    return children.filter((c) => answers.questions[c.code] === true).length;
  }

  if (parentType === 'checkbox_group' || parentType === 'multi_select') {
    return checkboxCount;
  }

  return answers.questions[parentQuestionCode] === true ? 1 : 0;
}

/** API may send `gte` / `lte` / `eq` instead of `>=` / `<=` / `===`. */
function compareCount(
  count: number,
  threshold: number,
  operator: string | null | undefined,
): boolean {
  const op = String(operator ?? '').toLowerCase();
  if (op === '>' || op === 'gt') return count > threshold;
  if (op === '>=' || op === 'gte') return count >= threshold;
  if (op === '<' || op === 'lt') return count < threshold;
  if (op === '<=' || op === 'lte') return count <= threshold;
  if (op === '===' || op === '=' || op === 'eq' || op === '==') return count === threshold;
  return count >= threshold;
}

export function evaluateCondition(
  condition: GqlCondition | null | undefined,
  answers: QuestionnaireAnswers,
  allQuestions?: AnyQuestion[],
): boolean {
  if (!condition) return true;
  const { parentQuestionCode, operator, value, countWhere } = condition;

  if (countWhere != null && countWhere.answer != null) {
    const count = countForParentCondition(parentQuestionCode, answers, allQuestions);
    const threshold = Number(value ?? 0);
    return compareCount(count, threshold, operator);
  }

  const answer = answers.questions[parentQuestionCode];
  const boolValue = value === 'true' || value === 'yes';
  const op = String(operator ?? '').toLowerCase();
  if (op === '===' || op === '=' || op === 'eq' || op === '==' || !operator) return answer === boolValue;
  if (op === '!==' || op === 'ne' || op === 'neq') return answer !== boolValue;
  return answer === boolValue;
}

export function isFollowUpVisible(
  followUp: GqlFollowUp | null | undefined,
  answers: QuestionnaireAnswers,
  allQuestions?: AnyQuestion[],
): boolean {
  if (!followUp) return false;
  return evaluateCondition(followUp.visibility, answers, allQuestions);
}

/** When true, required follow-up fields must be filled (visible + optional requireWhen). */
export function shouldRequireFollowUpFields(
  followUp: GqlFollowUp,
  answers: QuestionnaireAnswers,
  allQuestions: AnyQuestion[],
): boolean {
  if (!isFollowUpVisible(followUp, answers, allQuestions)) return false;
  if (followUp.requireWhen != null) {
    return evaluateCondition(followUp.requireWhen, answers, allQuestions);
  }
  return true;
}

function isFollowUpFieldRequired(field: GqlField, followUp: GqlFollowUp): boolean {
  if (field.required === false) return false;
  if (field.required === true) return true;
  return Boolean(followUp.required);
}

function isFollowUpFieldValueEmpty(field: GqlField, followUp: GqlFollowUp, answers: QuestionnaireAnswers): boolean {
  const raw = answers.followUpFields[followUp.code]?.[field.code];
  return String(raw ?? '').trim() === '';
}

/** Visit every question node that can carry answers or follow-ups (incl. checkbox_group rows). */
export function forEachQuestionNode(questions: AnyQuestion[], visit: (q: AnyQuestion) => void): void {
  for (const q of questions) {
    visit(q);
    const t = q.type?.toLowerCase() ?? '';
    const children = ('questions' in q ? (q as GqlQuestion).questions : undefined) ?? [];
    if (t === 'group_of_questions' || t === 'group' || t === 'section') {
      forEachQuestionNode(children as AnyQuestion[], visit);
    }
    if (t === 'checkbox_group' || t === 'multi_select') {
      for (const item of children) {
        visit(item as AnyQuestion);
      }
    }
  }
}

/**
 * Drops `followUpFields` entries whose follow-up is not visible for the current answers
 * (e.g. user switched YES → NO or unchecked a checkbox row).
 */
export function cleanupHiddenFollowUpFields(
  answers: QuestionnaireAnswers,
  allQuestions: AnyQuestion[],
): QuestionnaireAnswers {
  const nextFollowUp: Record<string, Record<string, string>> = { ...answers.followUpFields };
  let changed = false;

  forEachQuestionNode(allQuestions, (q) => {
    const followUp = 'followUp' in q ? q.followUp : undefined;
    if (!followUp?.code) return;
    if (isFollowUpVisible(followUp, answers, allQuestions)) return;
    if (nextFollowUp[followUp.code] != null) {
      delete nextFollowUp[followUp.code];
      changed = true;
    }
  });

  if (!changed) return answers;
  return { ...answers, followUpFields: nextFollowUp };
}

function appendFollowUpFieldErrors(
  q: AnyQuestion,
  answers: QuestionnaireAnswers,
  allQuestions: AnyQuestion[],
  errors: Set<string>,
): void {
  const followUp = 'followUp' in q ? q.followUp : undefined;
  if (!followUp?.fields?.length) return;
  if (!shouldRequireFollowUpFields(followUp, answers, allQuestions)) return;

  for (const field of followUp.fields) {
    if (isFollowUpFieldRequired(field, followUp) && isFollowUpFieldValueEmpty(field, followUp, answers)) {
      errors.add(q.code);
    }
  }
}

/**
 * Walk the question tree and return a Set of question codes that are
 * required but have not been answered (including required follow-up fields when applicable).
 */
export function collectValidationErrors(
  questions: AnyQuestion[],
  answers: QuestionnaireAnswers,
  allQuestions: AnyQuestion[] = questions,
): Set<string> {
  const errors = new Set<string>();

  forEachQuestionNode(questions, (q) => appendFollowUpFieldErrors(q, answers, allQuestions, errors));

  for (const q of questions) {
    const t = q.type?.toLowerCase();

    if (t === 'yes_no_question' || t === 'yes_no' || t === 'boolean') {
      if (q.required && answers.questions[q.code] == null) {
        errors.add(q.code);
      }
    } else if (t === 'group_of_questions') {
      const children = ('questions' in q ? (q as GqlQuestion).questions : undefined) ?? [];
      const childErrors = collectValidationErrors(children as AnyQuestion[], answers, allQuestions);
      childErrors.forEach((c) => errors.add(c));
    } else if (t === 'checkbox' || t === 'checkboxes') {
      if (q.required && (answers.checkboxes[q.code] ?? []).length === 0) {
        errors.add(q.code);
      }
    } else if (t === 'checkbox_group' || t === 'multi_select') {
      if (q.required && (answers.checkboxes[q.code] ?? []).length === 0) {
        errors.add(q.code);
      }
    } else if (t === 'group' || t === 'section') {
      const children = ('questions' in q ? (q as GqlQuestion).questions : undefined) ?? [];
      const childErrors = collectValidationErrors(children as AnyQuestion[], answers, allQuestions);
      childErrors.forEach((c) => errors.add(c));
    } else if (t === 'input_text' || t === 'input_number' || t === 'input_string') {
      if (q.required && !answers.textInputs[q.code]?.trim()) {
        errors.add(q.code);
      }
    } else if (t === 'table') {
      if (q.required) {
        const meta = getTableMeta(q);
        const rowDefs = meta.rows ?? [];
        const table = answers.tables[q.code] ?? {};
        const anyRowMissing = rowDefs.length === 0
          ? true
          : rowDefs.some((r) => !(table[r.code]?.length));
        if (anyRowMissing) {
          errors.add(q.code);
        }
      }
    } else if (q.required && answers.questions[q.code] == null) {
      errors.add(q.code);
    }
  }

  return errors;
}

export const CONFIRMATION_ERROR_KEY = '__confirmation__';
